import { t } from "../i18n/index.ts";
import { mountCollapseBanner, type CollapseBannerHandle } from "./editor-banner-collapse.ts";
import { el } from "./dom.ts";

export interface QuestRouteBannerOptions {
  questTitle: string;
  published: boolean;
  appRoot: HTMLElement;
  mapEl: HTMLElement;
  onDone: () => void;
}

let collapseHandle: CollapseBannerHandle | null = null;

export function destroyQuestRouteBanner(): void {
  collapseHandle?.destroy();
  collapseHandle = null;
  document.querySelector("#quest-route-banner")?.remove();
}

export function paintQuestRouteBanner(opts: QuestRouteBannerOptions): void {
  destroyQuestRouteBanner();
  const banner = document.createElement("div");
  banner.id = "quest-route-banner";
  banner.className = "quest-route-banner glass-panel editor-banner";

  const collapsed = document.createElement("div");
  collapsed.className = "editor-banner-collapsed";
  collapsed.tabIndex = 0;
  collapsed.setAttribute("role", "button");
  const line = document.createElement("span");
  line.className = "editor-banner-collapsed-line";
  const label = document.createElement("strong");
  label.textContent = t("qroute.title");
  const title = document.createElement("span");
  title.className = "editor-banner-quest-title";
  title.textContent = opts.questTitle.trim() || t("qbar.newQuest");
  const badge = document.createElement("span");
  badge.className = "editor-banner-badge quest-status-badge";
  const draft = !opts.published;
  badge.dataset.status = draft ? "draft" : "active";
  badge.textContent = t(draft ? "quests.draft" : "quests.published");
  for (const bit of [label, title, badge]) {
    if (line.lastChild) line.append(document.createTextNode(" · "));
    line.append(bit);
  }
  collapsed.append(line);

  const expanded = document.createElement("div");
  expanded.className = "editor-banner-expanded quest-route-banner-text";
  const heading = document.createElement("strong");
  heading.textContent = t("qroute.title");
  const sub = document.createElement("span");
  sub.textContent = opts.questTitle.trim()
    ? t("qroute.banner", { title: opts.questTitle.trim() })
    : t("qroute.bannerUntitled");
  expanded.append(heading, sub);

  const done = document.createElement("button");
  done.type = "button";
  done.id = "quest-route-done";
  done.className = "btn btn-primary editor-banner-done";
  done.textContent = t("qroute.done");
  done.addEventListener("click", () => opts.onDone());

  banner.append(collapsed, expanded, done);
  opts.appRoot.append(banner);

  collapseHandle = mountCollapseBanner(banner, [opts.mapEl, document]);
}

/** Collapsible admin live-edit warning in the quest editor guide column. */
export function createAdminPublishedBanner(questTitle: string): HTMLElement {
  const banner = document.createElement("div");
  banner.className = "qe-live-banner editor-banner editor-banner-admin";

  const collapsed = document.createElement("div");
  collapsed.className = "editor-banner-collapsed";
  collapsed.tabIndex = 0;
  collapsed.setAttribute("role", "button");
  const line = document.createElement("span");
  line.className = "editor-banner-collapsed-line";
  const label = document.createElement("strong");
  label.textContent = t("rt.adminPublished.collapsedLabel");
  const title = document.createElement("span");
  title.className = "editor-banner-quest-title";
  title.textContent = questTitle.trim() || t("qbar.newQuest");
  const badge = document.createElement("span");
  badge.className = "editor-banner-badge quest-status-badge";
  badge.dataset.status = "active";
  badge.textContent = t("quests.published");
  for (const bit of [label, title, badge]) {
    if (line.lastChild) line.append(document.createTextNode(" · "));
    line.append(bit);
  }
  collapsed.append(line);

  const expanded = document.createElement("div");
  expanded.className = "editor-banner-expanded";
  expanded.append(el("p", "", t("rt.adminPublished.banner")));

  banner.append(collapsed, expanded);
  return banner;
}
